import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Check, Flag, Home } from "lucide-react";
import type { RootState } from "@/store/index";
import { Button } from "@/components/ui/button";
import { ROUTES } from "@/routes/routeConfig";
import { NotFoundMascot } from "./NotFoundMascot";

const CSS = `
@keyframes nf-rise { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
.nf-rise { animation: nf-rise .5s ease-out both; }
@media (prefers-reduced-motion: reduce) { .nf-rise { animation: none; } }
`;

/** Where "Return to Dashboard" should go for the logged-in user's panel. */
function homeFor(role: string): string | null {
  switch (role) {
    case "SUPER_ADMIN": return ROUTES.SUPER_ADMIN.DASHBOARD;
    case "INSTITUTION": return ROUTES.INSTITUTION_ADMIN.DASHBOARD;
    case "TEACHER": return ROUTES.TEACHER_PORTAL.DASHBOARD;
    case "STUDENT": return ROUTES.STUDENT_PORTAL.DASHBOARD;
    case "ORG": return ROUTES.ORG_ADMIN.DASHBOARD;
    case "CAMPUS": return ROUTES.CAMPUS_ADMIN.DASHBOARD;
    case "ACCOUNTANT":
    case "LIBRARIAN":
    case "DYNAMIC": return ROUTES.STAFF_PORTAL.DASHBOARD;
    default: return null;
  }
}

/**
 * The one "page not found" screen for the whole app. Used as the app-wide catch-all (no layout) and inside every panel layout
 * (`inPanel`, so the sidebar and header stay visible). The main button goes to the dashboard of whoever is logged in.
 */
export default function NotFoundPage({ inPanel = false }: { inPanel?: boolean }) {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { user } = useSelector((s: RootState) => s.auth) as { user?: { role?: string } | null };
  const [copied, setCopied] = useState(false);

  const home = homeFor(String(user?.role ?? "").toUpperCase());
  const canGoBack = typeof window !== "undefined" && window.history.length > 1;
  const supportEmail = import.meta.env.VITE_SUPPORT_EMAIL as string | undefined;

  useEffect(() => {
    const previous = document.title;
    document.title = `${t("notFound.tabTitle", { defaultValue: "Page not found" })} · ${previous}`;
    return () => { document.title = previous; };
  }, [t]);

  async function report() {
    const url = `${window.location.origin}${pathname}${search}`;
    if (supportEmail) {
      const subject = encodeURIComponent(t("notFound.mailSubject", { defaultValue: "Broken link report" }));
      const body = encodeURIComponent(`${t("notFound.mailBody", { defaultValue: "This page could not be found:" })}\n${url}`);
      window.location.href = `mailto:${supportEmail}?subject=${subject}&body=${body}`;
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 3000);
    } catch {
      window.prompt(t("notFound.copyManually", { defaultValue: "Copy this link and send it to support:" }), url);
    }
  }

  return (
    <main className={`flex w-full flex-col items-center justify-center px-4 text-center ${inPanel ? "min-h-[70vh] py-8" : "min-h-screen bg-white py-10"}`}>
      <style>{CSS}</style>
      <NotFoundMascot />

      <span className="nf-rise mt-8 inline-flex items-center gap-1.5 rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-[11px] font-medium text-orange-700" style={{ animationDelay: "80ms" }}>
        <span className="h-1.5 w-1.5 rounded-full bg-orange-500" aria-hidden />
        {t("notFound.badge", { defaultValue: "Error 404 · Page not found" })}
      </span>

      <h1 className="nf-rise mt-4 max-w-xl text-2xl font-bold tracking-tight text-text-dark-dash sm:text-3xl" style={{ animationDelay: "140ms" }}>
        {t("notFound.title", { defaultValue: "Uh-oh… Looks like you wandered off campus." })}
      </h1>
      <p className="nf-rise mt-3 max-w-md text-sm leading-relaxed text-text-muted-dash" style={{ animationDelay: "200ms" }}>
        {t("notFound.body", { defaultValue: "The page you were heading to has been moved, removed, or never existed. Check the address, or head back to somewhere familiar." })}
      </p>
      <code className="nf-rise mt-3 max-w-full truncate rounded-md bg-brand-neutral-100 px-2.5 py-1 text-[11px] text-text-muted-dash" style={{ animationDelay: "240ms" }} title={pathname}>
        {pathname}
      </code>

      <div className="nf-rise mt-6 flex flex-wrap items-center justify-center gap-2.5" style={{ animationDelay: "280ms" }}>
        <Button onClick={() => navigate(home ?? ROUTES.LANDING, { replace: true })}>
          <Home className="mr-1.5 h-4 w-4" />
          {home ? t("notFound.dashboard", { defaultValue: "Return to Dashboard" }) : t("notFound.home", { defaultValue: "Go to home page" })}
        </Button>
        {canGoBack && (
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-1.5 h-4 w-4" /> {t("notFound.back", { defaultValue: "Go back" })}
          </Button>
        )}
        <Button variant="ghost" onClick={report}>
          {copied ? <Check className="mr-1.5 h-4 w-4 text-emerald-600" /> : <Flag className="mr-1.5 h-4 w-4" />}
          {copied ? t("notFound.copied", { defaultValue: "Link copied" }) : t("notFound.report", { defaultValue: "Report broken link" })}
        </Button>
      </div>
    </main>
  );
}